import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Plus, Calendar, MapPin, DollarSign, Users, Eye, Pencil, Trash2,
  TrendingUp, Ticket, PartyPopper
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const EVENT_CATEGORIES = ['pride', 'party', 'meetup', 'networking', 'sports', 'arts', 'community', 'dating'];

const AdminEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'party',
    event_date: '',
    start_time: '',
    end_time: '',
    location_name: '',
    location_address: '',
    is_paid: false,
    price: '',
    capacity: '',
    has_waitlist: true,
    is_featured: false,
    image_data: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/events/analytics/overview`)
      ]);
      setEvents(eventsRes.data.events || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'party',
      event_date: '',
      start_time: '',
      end_time: '',
      location_name: '',
      location_address: '',
      is_paid: false,
      price: '',
      capacity: '',
      has_waitlist: true,
      is_featured: false,
      image_data: ''
    });
    setEditingEvent(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      category: event.category,
      event_date: event.event_date,
      start_time: event.start_time,
      end_time: event.end_time || '',
      location_name: event.location_name,
      location_address: event.location_address,
      is_paid: event.is_paid,
      price: event.price?.toString() || '',
      capacity: event.capacity?.toString() || '',
      has_waitlist: event.has_waitlist,
      is_featured: event.is_featured || false,
      image_data: ''
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.event_date || !formData.start_time || !formData.location_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.is_paid && (!formData.price || parseFloat(formData.price) <= 0)) {
      toast.error('Paid events must have a price greater than 0');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: formData.is_paid ? parseFloat(formData.price) : null,
        capacity: formData.capacity ? parseInt(formData.capacity) : null
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success('Event created');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(f => ({ ...f, image_data: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Events Management</h1>
          <p className="text-[#A1A1AA]">Create and manage community events</p>
        </div>
        <Button 
          data-testid="create-event-btn"
          onClick={handleCreate}
          className="bg-[#FF2E63] hover:bg-[#E01E50]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-[#FF2E63]" />
              <span className="text-[#A1A1AA]">Total Events</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.events?.total || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <PartyPopper className="w-5 h-5 text-[#08D9D6]" />
              <span className="text-[#A1A1AA]">Upcoming</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.events?.upcoming || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Ticket className="w-5 h-5 text-[#7000FF]" />
              <span className="text-[#A1A1AA]">Registrations</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.registrations?.total || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-[#22c55e]" />
              <span className="text-[#A1A1AA]">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-white">${analytics.revenue?.total?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      )}

      {/* Events Table */}
      <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-[#A1A1AA] font-medium">Event</th>
                <th className="text-left p-4 text-[#A1A1AA] font-medium">Date</th>
                <th className="text-left p-4 text-[#A1A1AA] font-medium">Category</th>
                <th className="text-left p-4 text-[#A1A1AA] font-medium">Price</th>
                <th className="text-left p-4 text-[#A1A1AA] font-medium">Registrations</th>
                <th className="text-left p-4 text-[#A1A1AA] font-medium">Status</th>
                <th className="text-right p-4 text-[#A1A1AA] font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-[#A1A1AA]">
                    No events yet. Create your first event!
                  </td>
                </tr>
              ) : (
                events.map(event => (
                  <tr key={event.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-[#FF2E63]/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-[#FF2E63]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{event.title}</p>
                          <p className="text-sm text-[#52525B]">{event.location_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-[#A1A1AA]">{formatDate(event.event_date)}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-white/10 rounded text-xs text-white capitalize">
                        {event.category}
                      </span>
                    </td>
                    <td className="p-4 text-white font-medium">
                      {event.is_paid ? `$${event.price}` : 'Free'}
                    </td>
                    <td className="p-4 text-[#A1A1AA]">
                      {event.registered_count || 0}
                      {event.capacity && ` / ${event.capacity}`}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        event.is_active ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-red-500/10 text-red-500'
                      }`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/events/${event.id}`)}
                          className="text-[#A1A1AA] hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(event)}
                          className="text-[#A1A1AA] hover:text-white"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(event.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                data-testid="event-title-input"
                value={formData.title}
                onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                placeholder="Pride Night Party"
                className="bg-white/5 border-white/10"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                data-testid="event-description-input"
                value={formData.description}
                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                placeholder="Join us for an amazing celebration..."
                className="bg-white/5 border-white/10 min-h-[100px]"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={value => setFormData(f => ({ ...f, category: value }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {EVENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={e => setFormData(f => ({ ...f, event_date: e.target.value }))}
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={e => setFormData(f => ({ ...f, start_time: e.target.value }))}
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={e => setFormData(f => ({ ...f, end_time: e.target.value }))}
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Venue Name *</Label>
                <Input
                  value={formData.location_name}
                  onChange={e => setFormData(f => ({ ...f, location_name: e.target.value }))}
                  placeholder="Rainbow Club"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Address *</Label>
                <Input
                  value={formData.location_address}
                  onChange={e => setFormData(f => ({ ...f, location_address: e.target.value }))}
                  placeholder="123 Pride Street, SF"
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-[#22c55e]" />
                <div>
                  <p className="text-white font-medium">Paid Event</p>
                  <p className="text-sm text-[#A1A1AA]">Charge for tickets</p>
                </div>
              </div>
              <Switch
                checked={formData.is_paid}
                onCheckedChange={checked => setFormData(f => ({ ...f, is_paid: checked }))}
              />
            </div>

            {formData.is_paid && (
              <div className="space-y-2">
                <Label>Ticket Price (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={e => setFormData(f => ({ ...f, price: e.target.value }))}
                  placeholder="25.00"
                  className="bg-white/5 border-white/10"
                />
              </div>
            )}

            {/* Capacity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Capacity (leave empty for unlimited)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={e => setFormData(f => ({ ...f, capacity: e.target.value }))}
                  placeholder="100"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="flex items-center gap-3 pt-8">
                <Switch
                  checked={formData.has_waitlist}
                  onCheckedChange={checked => setFormData(f => ({ ...f, has_waitlist: checked }))}
                />
                <Label>Enable Waitlist</Label>
              </div>
            </div>

            {/* Featured */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-[#FF2E63]" />
                <div>
                  <p className="text-white font-medium">Featured Event</p>
                  <p className="text-sm text-[#A1A1AA]">Highlight at top of listings</p>
                </div>
              </div>
              <Switch
                checked={formData.is_featured}
                onCheckedChange={checked => setFormData(f => ({ ...f, is_featured: checked }))}
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Event Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="bg-white/5 border-white/10"
              />
              {formData.image_data && (
                <img src={formData.image_data} alt="Preview" className="w-full h-40 object-cover rounded-lg mt-2" />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              {saving ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEvents;
